import dayjs from 'dayjs';
import type { ChartDateBound } from './chartWhere.types';
import { isChartRelativeBound } from './chartWhere.types';

/** Converts a chart `where` JSON bound to a YYYY-MM-DD string. */
function resolveBoundToYmd(bound: ChartDateBound | undefined): string | undefined {
	if (bound === undefined || bound === null) {
		return undefined;
	}
	if (typeof bound === 'string') {
		const t = bound.trim();
		return t ? t : undefined;
	}
	if (isChartRelativeBound(bound)) {
		return dayjs().startOf('day').add(bound.relativeDays, 'day').format('YYYY-MM-DD');
	}
	return undefined;
}

/**
 * Turns stored chart `where` JSON into the API `dataFilter` shape:
 * date/datetime: resolved string `start` / `end` in `YYYY-MM-DD` form;
 * belongsTo: `{ targetEntryId }` passed through unchanged.
 */
export function resolveChartWhereForApi(whereJson: string | undefined | null): string | undefined {
	if (whereJson == null || !String(whereJson).trim()) {
		return undefined;
	}
	let doc: unknown;
	try {
		doc = JSON.parse(String(whereJson));
	} catch {
		return undefined;
	}
	if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
		return undefined;
	}
	const out: Record<
		string,
		{ start?: string; end?: string } | { isUnset: true } | { targetEntryId: string }
	> = {};
	for (const [slug, spec] of Object.entries(doc as Record<string, unknown>)) {
		if (!slug || spec === null || typeof spec !== 'object' || Array.isArray(spec)) {
			continue;
		}
		const o = spec as Record<string, unknown>;
		if (o.isUnset === true) {
			out[slug] = { isUnset: true };
			continue;
		}
		const te = o.targetEntryId;
		if (typeof te === 'string' && te.trim() && o.start === undefined && o.end === undefined) {
			out[slug] = { targetEntryId: te.trim() };
			continue;
		}
		const { start, end } = o;
		const startYmd = resolveBoundToYmd(start as ChartDateBound | undefined);
		const endYmd = resolveBoundToYmd(end as ChartDateBound | undefined);
		if (startYmd === undefined && endYmd === undefined) {
			continue;
		}
		const range: { start?: string; end?: string } = {};
		if (startYmd !== undefined) {
			range.start = startYmd;
		}
		if (endYmd !== undefined) {
			range.end = endYmd;
		}
		out[slug] = range;
	}
	const keys = Object.keys(out);
	return keys.length ? JSON.stringify(out) : undefined;
}

/** Reads `start` / `end` YYYY-MM-DD strings for one slug from resolved API `dataFilter` JSON. */
export function parseResolvedChartDataFilterBoundsForSlug(
	dataFilterJson: string | undefined,
	slug: string,
): { start?: string; end?: string } | null {
	if (!dataFilterJson?.trim() || !slug.trim()) {
		return null;
	}
	try {
		const doc = JSON.parse(dataFilterJson) as Record<string, unknown>;
		const raw = doc[slug.trim()];
		if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
			return null;
		}
		const rawObj = raw as Record<string, unknown>;
		if (rawObj.isUnset === true) {
			return null;
		}
		const { start, end } = rawObj;
		const out: { start?: string; end?: string } = {};
		if (typeof start === 'string' && start.trim()) {
			out.start = start.trim().slice(0, 10);
		}
		if (typeof end === 'string' && end.trim()) {
			out.end = end.trim().slice(0, 10);
		}
		return out.start || out.end ? out : null;
	} catch {
		return null;
	}
}
